import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { STATUS_STYLES, JOB_TYPE_LABELS, fmtDate, fmtTime } from "@/lib/helpers";
import { ArrowLeft, Loader2, Building2, User, Phone, Mail, MapPin, Briefcase, Pencil, Plus } from "lucide-react";

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [c, j] = await Promise.all([api.get(`/clients/${id}`), api.get("/jobs")]);
      setClient(c.data);
      setJobs(j.data.filter((job) => job.client_id === id));
    } catch { setClient(null); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;
  if (!client) return <div className="py-24 text-center text-slate-500">Client not found. <button className="text-lime-600 underline" onClick={() => navigate("/clients")}>Back to clients</button></div>;

  const activeJobs = jobs.filter((j) => !["completed", "archived", "deleted"].includes(j.pipeline_stage));

  return (
    <div className="space-y-6 fade-up" data-testid="client-detail-page">
      <button onClick={() => navigate("/clients")} data-testid="client-back-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> All clients
      </button>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {/* Client info */}
        <div className="bg-slate-900 text-white rounded-2xl p-6 lg:sticky lg:top-24">
          <div className="flex items-center gap-2 mb-3"><Building2 className="h-6 w-6 text-lime-400" /><h1 className="text-2xl font-bold font-heading">{client.name}</h1></div>
          <div className="space-y-2 text-sm text-slate-200">
            {client.contact_name && <p className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" />{client.contact_name}</p>}
            {client.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /><a href={`tel:${client.phone}`} className="hover:text-lime-400">{client.phone}</a></p>}
            {client.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><a href={`mailto:${client.email}`} className="hover:text-lime-400 truncate">{client.email}</a></p>}
            {client.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /><span>{client.address}</span></p>}
          </div>
          {client.notes && <p className="mt-4 text-xs text-slate-400 border-t border-slate-700 pt-3">{client.notes}</p>}
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="bg-slate-800 rounded-lg p-3"><p className="text-2xl font-bold font-heading text-lime-400">{jobs.length}</p><p className="text-xs text-slate-400">Total jobs</p></div>
            <div className="bg-slate-800 rounded-lg p-3"><p className="text-2xl font-bold font-heading text-lime-400">{activeJobs.length}</p><p className="text-xs text-slate-400">Active</p></div>
          </div>
        </div>

        {/* Jobs */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900 font-heading flex items-center gap-2"><Briefcase className="h-5 w-5 text-lime-500" /> Jobs ({jobs.length})</h2>
            <button onClick={() => navigate(`/jobs/new?client=${id}`)} data-testid="client-new-job-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> New Job</button>
          </div>
          {jobs.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500">No jobs for this client yet.</div>
          ) : jobs.map((job) => {
            const st = STATUS_STYLES[job.status] || STATUS_STYLES.scheduled;
            return (
              <button key={job.id} onClick={() => navigate(`/jobs/${job.id}`)} data-testid={`client-job-${job.id}`}
                className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-slate-300 transition-all">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-semibold text-slate-900">{job.title}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${st.bg} ${st.text} ${st.border}`}>{st.label}</span>
                      {job.pipeline_stage && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{job.pipeline_stage}</span>}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{JOB_TYPE_LABELS[job.job_type] || job.job_type}{job.location ? ` · ${job.location}` : ""}</p>
                  </div>
                  <p className="text-sm text-slate-500 shrink-0">{fmtDate(job.start)} · {fmtTime(job.start)}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
